import { analyzeQuery, indexFieldSummary } from "./indexAdvisorService";
import { estimateOperationCost } from "./costEstimatorService";
import { buildQueryString } from "./queryEngineService";

/**
 * Build a Query Explain report from a query plan, execution metrics and any
 * client-side refinement that ran afterwards. Mirrors the "Explain" panels of
 * Firebase Console / Studio 3T.
 */
export function buildExplain({ plan, metrics = {}, timings = {}, clientRefined = 0, error = null, missingIndex = null }) {
  const analysis = plan
    ? analyzeQuery({
        collectionName: plan.collectionName,
        clauses: (plan.serverClauses || []).map((c) => ({ field: c.field, op: c.op })),
        orderByField: plan.sortField === "id" ? "" : plan.sortField,
        orderByDir: plan.sortAsc ? "asc" : "desc",
      })
    : { needsComposite: false, index: null, warnings: [] };

  const indexesUsed = analysis.needsComposite && analysis.index
    ? analysis.index.fields.map(indexFieldSummary)
    : ["Single-field automatic index"];

  const docsReturned = metrics.docsReturned ?? 0;
  const estimatedReads = metrics.estimatedReads ?? docsReturned ?? 0;
  const estimatedCost = metrics.estimatedCost ?? estimateOperationCost({ reads: estimatedReads }).estimatedTotalUsd;

  const queryTimeMs = timings.queryTimeMs ?? metrics.queryTimeMs ?? 0;
  const downloadTimeMs = timings.downloadTimeMs ?? 0;
  const processingTimeMs = timings.processingTimeMs ?? 0;
  const renderTimeMs = timings.renderTimeMs ?? 0;
  const networkTimeMs = timings.networkTimeMs ?? queryTimeMs;
  const totalTimeMs = timings.totalTimeMs ?? (queryTimeMs + processingTimeMs + renderTimeMs);

  return {
    mode: plan?.mode || "client",
    firestoreQuery: plan ? buildQueryString(plan) : "Client-side query (no server constraints)",
    indexesUsed,
    needsComposite: analysis.needsComposite,
    estimatedReads,
    documentsReturned: docsReturned,
    clientRefined,
    estimatedCostUsd: estimatedCost,
    warnings: [...(plan?.warnings || []), ...(analysis.warnings || [])],
    performance: {
      queryTimeMs,
      downloadTimeMs,
      processingTimeMs,
      renderTimeMs,
      networkTimeMs,
      totalTimeMs,
      latencyMs: networkTimeMs,
    },
    error,
    missingIndex,
  };
}

export function formatCostUsd(usd) {
  if (!usd) return "$0.000000";
  if (usd < 0.000001) return "< $0.000001";
  return `$${usd.toFixed(6)}`;
}
